import { FormikProvider, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import { Stack, TextField, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { createWithURLSearchParams, editItem } from '../../../fetch';
import { useTranslation } from '../../../common/LocalizationProvider';
import { groupsActions, sessionActions } from '../../../store';

// ----------------------------------------------------------------------

export default function RegisterForm() {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };
  const handleShowPasswordConfirm = () => {
    setShowPasswordConfirm((show) => !show);
  };

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().email(t('RGF_Valid_Email')).required(t('RGF_Email_Required')),
    password: Yup.string().required(t('RPF_Password_Required')),
    confirmPassword: Yup.string()
      .required(t('RPF_Password_Required'))
      .oneOf([Yup.ref('password'), null], t('RPF_Valid_Password')),
    prenom: Yup.string()
      .min(2, t('yupValid_Short'))
      .max(50, t('yupValid_Long'))
      .required(t('yupValid_Required')),
    name: Yup.string()
      .min(2, t('yupValid_Short'))
      .max(50, t('yupValid_Long'))
      .required(t('yupValid_Required'))
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      prenom: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      const body = {
        name: values.name,
        prenom: values.prenom,
        email: values.email,
        password: values.password,
        uniteAltitude: 'ft',
        uniteDistance: 'mille',
        uniteTemp: '°C',
        uniteVitesse: 'kt',
        attributes: { geofenceFlag: true },
        map: 'Carto'
      };
      const response = await editItem('users', undefined, body);
      if (response.ok) {
        const responseSession = await createWithURLSearchParams(
          'session',
          `email=${values.email}&password=${values.password}`
        );

        if (responseSession.ok) {
          window.localStorage.clear();

          const result = await responseSession.json();
          dispatch(sessionActions.update(result));
          dispatch(sessionActions.authenticated(true));
          if (!result.administrator) {
            if (window.localStorage.getItem('groupId')) {
              dispatch(groupsActions.select(JSON.parse(window.localStorage.getItem('groupId'))));
            } else {
              dispatch(
                groupsActions.select(
                  result.permission && result.permission[0] && result.permission[0].groupid
                    ? result.permission[0].groupid
                    : 0
                )
              );
            }
          } else {
            dispatch(groupsActions.select(0));
          }

          navigate('/dashboard', { replace: true });
        } else {
          setSubmitting(false);
          setErrors({ email: 'Invalid', password: 'Invalid' });
        }
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, setErrors, setSubmitting } =
    formik;

  return (
    <FormikProvider value={formik}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('fields_FirstName')}
            {...getFieldProps('prenom')}
            value={getFieldProps('prenom').value || ''}
            error={Boolean(touched.prenom && errors.prenom)}
            helperText={touched.prenom && errors.prenom}
          />

          <TextField
            fullWidth
            label={t('fields_LastName')}
            {...getFieldProps('name')}
            value={getFieldProps('name').value || ''}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />
        </Stack>
        <TextField
          fullWidth
          autoComplete="email"
          type="email"
          label={t('fields_Email')}
          {...getFieldProps('email')}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
        />

        <TextField
          fullWidth
          autoComplete="current-password"
          type={showPassword ? 'text' : 'password'}
          label={t('RPF_Password')}
          {...getFieldProps('password')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleShowPassword} edge="end">
                  <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                </IconButton>
              </InputAdornment>
            )
          }}
          error={Boolean(touched.password && errors.password)}
          helperText={touched.password && errors.password}
        />
        <TextField
          fullWidth
          autoComplete="current-password"
          type={showPasswordConfirm ? 'text' : 'password'}
          label={t('RPF_ConfirmPassword')}
          {...getFieldProps('confirmPassword')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleShowPasswordConfirm} edge="end">
                  <Icon icon={showPasswordConfirm ? eyeFill : eyeOffFill} />
                </IconButton>
              </InputAdornment>
            )
          }}
          error={Boolean(touched.confirmPassword && errors.confirmPassword)}
          helperText={touched.confirmPassword && errors.confirmPassword}
        />

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('RG_Tab_O')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
